import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Mail,
  RefreshCw,
  Search,
  Send,
  Upload,
  X,
} from "lucide-react";
import Button from "../components/Button";
import PageShell from "../components/PageShell";
import StatusBanner from "../components/StatusBanner";
import { calculateFinalScore, formatPercent, formatRating, formatScore } from "../lib/scoringUtils";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import * as XLSX from "xlsx";

const initialCycleForm = {
  cycle_name: "",
  opens_at: "",
  closes_at: "",
};

const importRequiredColumns = [
  "mentee_name", "mentee_roll_no", "mentee_email",
  "mentee_phone", "mentee_branch",
  "mentor_name", "mentor_roll",
  "mentor_email", "mentor_phone", "mentor_branch",
];

// Maps common header variations to the canonical column names above
const columnAliases = {
  mentee_roll: "mentee_roll_no",
  mentee_roll_number: "mentee_roll_no",
  mentee_rollno: "mentee_roll_no",
  mentee_ph: "mentee_phone",
  mentee_ph_number: "mentee_phone",
  mentee_phone_number: "mentee_phone",
  alloted_mentor_name: "mentor_name",
  allotted_mentor_name: "mentor_name",
  allocated_mentor_name: "mentor_name",
  mentor_roll_no: "mentor_roll",
  mentor_roll_number: "mentor_roll",
  mentor_rollno: "mentor_roll",
  mentor_ph: "mentor_phone",
  mentor_ph_number: "mentor_phone",
  mentor_phone_number: "mentor_phone",
  mentor_email_id: "mentor_email",
  mentor_emailid: "mentor_email",
  alloted_mentor_email: "mentor_email",
  allotted_mentor_email: "mentor_email",
};

const BATCH_SIZE = 50;

function isValidSessionName(name) {
  const match = name.trim().match(/^(\d{4})-(\d{2})$/);
  if (!match) return false;
  const year = parseInt(match[1], 10);
  const suffix = parseInt(match[2], 10);
  return suffix === (year + 1) % 100;
}

function normalizeHeader(key) {
  const raw = key.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  return columnAliases[raw] || raw;
}

function toDateTimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function toIsoFromLocal(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function parseCsv(text) {
  const rows = [];
  let current = "";
  let row = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"' && next === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      row.push(current.trim());
      current = "";
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(current.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      current = "";
    } else {
      current += char;
    }
  }

  row.push(current.trim());
  if (row.some(Boolean)) rows.push(row);

  if (!rows.length) return [];

  const headers = rows[0].map((header) => header.trim().toLowerCase());
  return rows.slice(1).map((values) =>
    headers.reduce((record, header, index) => {
      record[header] = values[index] || "";
      return record;
    }, {}),
  );
}

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(index, index + chunkSize));
  }

  return window.btoa(binary);
}

function getAverage(feedback) {
  const ratings = [
    feedback.q1_rating,
    feedback.q2_rating,
    feedback.q3_rating,
    feedback.q4_rating,
    feedback.q5_rating,
  ].filter((rating) => rating !== null && rating !== undefined);

  if (!ratings.length) return null;
  return ratings.reduce((sum, rating) => sum + Number(rating), 0) / ratings.length;
}

// Bypasses Supabase PostgREST default 1,000-row limit by fetching in pages
async function fetchAllRows(fetchPageFn) {
  let allData = [];
  let from = 0;
  const step = 1000;
  while (true) {
    const { data, error } = await fetchPageFn(from, from + step - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    allData = allData.concat(data);
    if (data.length < step) break;
    from += step;
  }
  return allData;
}

export default function AdminPanel() {
  const [sessions, setSessions] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [cycleForm, setCycleForm] = useState(initialCycleForm);
  const [importSessionName, setImportSessionName] = useState("");
  const [performanceSessionId, setPerformanceSessionId] = useState("");
  const [performanceCycles, setPerformanceCycles] = useState([]);
  const [importRows, setImportRows] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [modalFeedbacks, setModalFeedbacks] = useState([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [snapshotExists, setSnapshotExists] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingCycle, setIsSavingCycle] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Leaderboard Search, Sort & Pagination State
  const [leaderboardSearch, setLeaderboardSearch] = useState("");
  const [leaderboardSort, setLeaderboardSort] = useState("worst_first");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Email Communications State
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isQueueing, setIsQueueing] = useState(false);
  const [selectedMenteeIds, setSelectedMenteeIds] = useState([]);
  const [allMentees, setAllMentees] = useState([]);
  const [defaulterMentees, setDefaulterMentees] = useState([]);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const recipientsRef = useRef(null);

  const selectedSession = sessions.find((session) => session.id === selectedSessionId);
  const selectedCycle = cycles.find((cycle) => cycle.id === selectedCycleId);
  const sessionExists = sessions.some((s) => s.session_name === importSessionName.trim());

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (!recipientsOpen) return undefined;

    function handleClickOutside(event) {
      if (recipientsRef.current && !recipientsRef.current.contains(event.target)) {
        setRecipientsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [recipientsOpen]);

  useEffect(() => {
    if (selectedSessionId) {
      loadSessionData(selectedSessionId);
    }
  }, [selectedSessionId]);

  useEffect(() => {
    if (performanceSessionId) {
      setCyclesReady(false);
      supabase
        .from("feedback_cycles")
        .select("id, cycle_name, closes_at")
        .eq("session_id", performanceSessionId)
        .order("closes_at", { ascending: false })
        .then(({ data }) => {
          const rows = data || [];
          setPerformanceCycles(rows);

          const now = new Date();
          const mostRecentClosed = rows.find((c) => new Date(c.closes_at) <= now);
          if (mostRecentClosed) {
            setSelectedCycleId(mostRecentClosed.id);
          } else if (rows.length) {
            setSelectedCycleId(rows[0].id);
          } else {
            setSelectedCycleId("");
          }
          setCyclesReady(true);
        });
    }
  }, [performanceSessionId]);

  // Check if a snapshot already exists for the selected cycle
  useEffect(() => {
    if (!selectedCycleId) {
      setSnapshotExists(false);
      return;
    }
    supabase
      .from("leaderboard_snapshots")
      .select("id")
      .eq("cycle_id", selectedCycleId)
      .limit(1)
      .then(({ data }) => setSnapshotExists(!!(data && data.length)));
  }, [selectedCycleId, leaderboardRows]);

  // Only load performance data once the cycle dropdown has been initialised.
  // Without this guard, the effect fires with selectedCycleId === "" while
  // performanceCycles is being populated, causing Path B (live computation)
  // to run with an empty cycle list and produce 0 scores.
  const [cyclesReady, setCyclesReady] = useState(false);
  useEffect(() => {
    if (performanceSessionId && cyclesReady) {
      loadPerformanceData();
    }
  }, [performanceSessionId, selectedCycleId, cyclesReady]);

  async function loadAdminData() {
    setIsLoading(true);
    setError(null);

    try {
      if (!isSupabaseConfigured) {
        setError("Supabase is not configured yet.");
        return;
      }

      const { data, error: sessionError } = await supabase
        .from("academic_sessions")
        .select("id, session_name, is_current")
        .order("session_name", { ascending: false });

      if (sessionError) throw sessionError;

      const sessionRows = data || [];
      setSessions(sessionRows);

      const currentSession = sessionRows.find((session) => session.is_current) || sessionRows[0];
      setSelectedSessionId(currentSession?.id || "");
      if (currentSession?.id && !performanceSessionId) {
        setPerformanceSessionId(currentSession.id);
      }
    } catch (caughtError) {
      setError(caughtError.message || "Could not load admin data.");
    } finally {
      setIsLoading(false);
    }
  }

  async function loadSessionData(sessionId) {
    setError(null);

    try {
      const { data, error: cycleError } = await supabase
        .from("feedback_cycles")
        .select("id, cycle_name, opens_at, closes_at, session_id")
        .eq("session_id", sessionId)
        .order("opens_at", { ascending: false });

      if (cycleError) throw cycleError;

      const cycleRows = data || [];
      setCycles(cycleRows);
      setSelectedCycleId((current) => {
        if (current && cycleRows.some((cycle) => cycle.id === current)) return current;
        return cycleRows[0]?.id || "";
      });
    } catch (caughtError) {
      setCycles([]);
      setSelectedCycleId("");
      setError(caughtError.message || "Could not load feedback cycles.");
    }
  }

  async function loadPerformanceData() {
    setError(null);

    try {
      // ── Path A: Specific cycle selected → read from leaderboard_snapshots ──
      if (selectedCycleId) {
        const snapshots = await fetchAllRows((from, to) =>
          supabase
            .from("leaderboard_snapshots")
            .select(
              "id, mentor_id, session_id, cycle_id, review, engagement_score, final_score, mentors(name, roll_number)",
            )
            .eq("cycle_id", selectedCycleId)
            .order("final_score", { ascending: true })
            .range(from, to),
        );

        if (snapshots && snapshots.length) {
          setLeaderboardRows(snapshots);

          // Still need to calculate defaulters for the email section,
          // even though we got leaderboard data from the snapshot.
          const mentees = await fetchAllRows((from, to) =>
            supabase
              .from("mentees")
              .select("id, name, roll_number, mentor_id, mentors(id, name, roll_number), users(email)")
              .eq("session_id", performanceSessionId)
              .range(from, to),
          );

          const feedbacks = await fetchAllRows((from, to) =>
            supabase
              .from("feedbacks")
              .select("id, mentor_id, mentee_id, cycle_id")
              .eq("cycle_id", selectedCycleId)
              .range(from, to),
          );

          setAllMentees(mentees || []);
          const defaulters = (mentees || []).filter((mentee) => {
            const hasSubmitted = (feedbacks || []).some((f) => f.mentee_id === mentee.id);
            return !hasSubmitted;
          });
          setDefaulterMentees(defaulters);
          setSelectedMenteeIds(defaulters.map((m) => m.id));
          setFeedbackRows([]);
          return;
        }

        // No snapshots yet — fall through to live computation so the admin
        // can still see a preview before generating the snapshot.
      }

      // ── Path B: "Overall session" or no snapshot yet → compute live ──
      // Get all mentees for this session (to calculate engagement per mentor)
      const mentees = await fetchAllRows((from, to) =>
        supabase
          .from("mentees")
          .select("id, name, roll_number, mentor_id, mentors(id, name, roll_number), users(email)")
          .eq("session_id", performanceSessionId)
          .range(from, to),
      );

      if (!mentees || !mentees.length) {
        setLeaderboardRows([]);
        setFeedbackRows([]);
        return;
      }

      // Extract unique mentors from the mentees relationships
      const mentorMap = new Map();
      mentees.forEach((m) => {
        if (m.mentors && !mentorMap.has(m.mentors.id)) {
          mentorMap.set(m.mentors.id, m.mentors);
        }
      });
      const mentors = Array.from(mentorMap.values());

      // Get feedbacks filtered by cycle or all session cycles
      let feedbacks = [];
      if (selectedCycleId) {
        feedbacks = await fetchAllRows((from, to) =>
          supabase
            .from("feedbacks")
            .select(
              "id, mentor_id, mentee_id, cycle_id, q1_rating, q2_rating, q3_rating, q4_rating, q5_rating, created_at, mentees(name, roll_number)",
            )
            .eq("cycle_id", selectedCycleId)
            .order("created_at", { ascending: false })
            .range(from, to),
        );
      } else {
        const sessionCycleIds = performanceCycles.map((cycle) => cycle.id);
        if (sessionCycleIds.length) {
          feedbacks = await fetchAllRows((from, to) =>
            supabase
              .from("feedbacks")
              .select(
                "id, mentor_id, mentee_id, cycle_id, q1_rating, q2_rating, q3_rating, q4_rating, q5_rating, created_at, mentees(name, roll_number)",
              )
              .in("cycle_id", sessionCycleIds)
              .order("created_at", { ascending: false })
              .range(from, to),
          );
        } else {
          setLeaderboardRows([]);
          setFeedbackRows([]);
          return;
        }
      }

      // Calculate mentees and defaulters for email communications
      setAllMentees(mentees || []);
      const sessionCycleIds = performanceCycles.map((cycle) => cycle.id);
      const relevantCycleIds = selectedCycleId ? [selectedCycleId] : sessionCycleIds;
      const defaulters = (mentees || []).filter((mentee) => {
        const hasSubmitted = (feedbacks || []).some((f) => f.mentee_id === mentee.id && relevantCycleIds.includes(f.cycle_id));
        return !hasSubmitted;
      });
      setDefaulterMentees(defaulters);
      setSelectedMenteeIds(defaulters.map((m) => m.id));

      // Calculate leaderboard from raw data
      const leaderboard = mentors.map((mentor) => {
        const mentorMentees = (mentees || []).filter((m) => m.mentor_id === mentor.id);
        const mentorFeedbacks = (feedbacks || []).filter((f) => f.mentor_id === mentor.id);

        let totalRating = 0;
        let ratingCount = 0;
        mentorFeedbacks.forEach((fb) => {
          const fbRatings = [fb.q1_rating, fb.q2_rating, fb.q3_rating, fb.q4_rating, fb.q5_rating]
            .filter((r) => r !== null && r !== undefined);
          fbRatings.forEach((r) => { totalRating += Number(r); ratingCount += 1; });
        });
        const avgRating = ratingCount > 0 ? totalRating / ratingCount : 0;

        const totalMenteeCount = mentorMentees.length;

        let engagementScore;
        if (!selectedCycleId && performanceCycles.length) {
          // Overall session: average engagement across all cycles
          const cycleEngagements = performanceCycles.map((cycle) => {
            const distinctRespondents = new Set(
              mentorFeedbacks.filter((f) => f.cycle_id === cycle.id).map((f) => f.mentee_id),
            ).size;
            return totalMenteeCount > 0 ? (distinctRespondents / totalMenteeCount) * 100 : 0;
          });
          engagementScore = cycleEngagements.reduce((a, b) => a + b, 0) / cycleEngagements.length;
        } else {
          // Single cycle: distinct respondents / total mentees (matches the RPC)
          const distinctRespondents = new Set(mentorFeedbacks.map((f) => f.mentee_id)).size;
          engagementScore = totalMenteeCount > 0 ? (distinctRespondents / totalMenteeCount) * 100 : 0;
        }

        const finalScore = calculateFinalScore({
          averageRating: avgRating,
          engagementScore,
        });

        return {
          id: mentor.id,
          mentor_id: mentor.id,
          mentors: { name: mentor.name, roll_number: mentor.roll_number },
          review: avgRating,
          engagement_score: engagementScore,
          final_score: finalScore,
        };
      });

      // Sort worst performers first for admin review
      leaderboard.sort((a, b) => a.final_score - b.final_score);

      setLeaderboardRows(leaderboard);
      setFeedbackRows(feedbacks || []);
    } catch (caughtError) {
      setLeaderboardRows([]);
      setFeedbackRows([]);
      setAllMentees([]);
      setDefaulterMentees([]);
      setError(caughtError.message || "Could not load mentor performance.");
    }
  }

  // Filter and sort leaderboard rows based on user search and sort selection
  const filteredAndSortedLeaderboard = useMemo(() => {
    let list = [...leaderboardRows];

    if (leaderboardSearch.trim()) {
      const q = leaderboardSearch.trim().toLowerCase();
      list = list.filter((row) => {
        const name = String(row.mentors?.name || "").toLowerCase();
        const roll = String(row.mentors?.roll_number || "").toLowerCase();
        return name.includes(q) || roll.includes(q);
      });
    }

    switch (leaderboardSort) {
      case "best_first":
        list.sort((a, b) => (b.final_score ?? 0) - (a.final_score ?? 0));
        break;
      case "worst_first":
        list.sort((a, b) => (a.final_score ?? 0) - (b.final_score ?? 0));
        break;
      case "name_asc":
        list.sort((a, b) => (a.mentors?.name || "").localeCompare(b.mentors?.name || ""));
        break;
      case "name_desc":
        list.sort((a, b) => (b.mentors?.name || "").localeCompare(a.mentors?.name || ""));
        break;
      case "rating_desc":
        list.sort((a, b) => (b.review ?? 0) - (a.review ?? 0));
        break;
      case "engagement_desc":
        list.sort((a, b) => (b.engagement_score ?? 0) - (a.engagement_score ?? 0));
        break;
      default:
        break;
    }

    return list;
  }, [leaderboardRows, leaderboardSearch, leaderboardSort]);

  const totalLeaderboardPages = Math.max(1, Math.ceil(filteredAndSortedLeaderboard.length / pageSize));

  const paginatedLeaderboardRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedLeaderboard.slice(start, start + pageSize);
  }, [filteredAndSortedLeaderboard, currentPage, pageSize]);



  async function handleSetCurrentSession() {
    if (!selectedSessionId) return;
    setError(null);
    setMessage(null);

    try {
      const { error: clearError } = await supabase
        .from("academic_sessions")
        .update({ is_current: false })
        .neq("id", selectedSessionId);

      if (clearError) throw clearError;

      const { error: currentError } = await supabase
        .from("academic_sessions")
        .update({ is_current: true })
        .eq("id", selectedSessionId);

      if (currentError) throw currentError;

      setMessage("Current academic session updated.");
      await loadAdminData();
    } catch (caughtError) {
      setError(caughtError.message || "Could not update the current session.");
    }
  }

  async function handleCreateCycle(event) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!selectedSessionId || !cycleForm.cycle_name || !cycleForm.opens_at || !cycleForm.closes_at) {
      setError("Select a session and fill all cycle fields.");
      return;
    }

    if (new Date(cycleForm.opens_at) >= new Date(cycleForm.closes_at)) {
      setError("Cycle close time must be after open time.");
      return;
    }

    setIsSavingCycle(true);

    try {
      const { error: insertError } = await supabase.from("feedback_cycles").insert({
        session_id: selectedSessionId,
        cycle_name: cycleForm.cycle_name.trim(),
        opens_at: toIsoFromLocal(cycleForm.opens_at),
        closes_at: toIsoFromLocal(cycleForm.closes_at),
      });

      if (insertError) throw insertError;

      setCycleForm(initialCycleForm);
      setMessage("Feedback cycle scheduled.");
      await loadSessionData(selectedSessionId);

      // Refresh performance cycles dropdown if it's the same session
      if (performanceSessionId === selectedSessionId) {
        setCyclesReady(false);
        const { data } = await supabase
          .from("feedback_cycles")
          .select("id, cycle_name, closes_at")
          .eq("session_id", performanceSessionId)
          .order("closes_at", { ascending: false });

        const rows = data || [];
        setPerformanceCycles(rows);
        const now = new Date();
        const mostRecentClosed = rows.find((c) => new Date(c.closes_at) <= now);
        if (mostRecentClosed) {
          setSelectedCycleId(mostRecentClosed.id);
        } else if (rows.length) {
          setSelectedCycleId(rows[0].id);
        } else {
          setSelectedCycleId("");
        }
        setCyclesReady(true);
      }
    } catch (caughtError) {
      setError(caughtError.message || "Could not create feedback cycle.");
    } finally {
      setIsSavingCycle(false);
    }
  }

  async function refreshPerformanceCyclesDropdown() {
    if (performanceSessionId !== selectedSessionId) return;

    setCyclesReady(false);
    const { data } = await supabase
      .from("feedback_cycles")
      .select("id, cycle_name, closes_at")
      .eq("session_id", performanceSessionId)
      .order("closes_at", { ascending: false });

    const rows = data || [];
    setPerformanceCycles(rows);
    const now = new Date();
    const mostRecentClosed = rows.find((c) => new Date(c.closes_at) <= now);
    if (mostRecentClosed) {
      setSelectedCycleId(mostRecentClosed.id);
    } else if (rows.length) {
      setSelectedCycleId(rows[0].id);
    } else {
      setSelectedCycleId("");
    }
    setCyclesReady(true);
  }

  async function handleEndCycle(cycleId, cycleName) {
    if (!window.confirm(`End "${cycleName}" now? Feedback submissions will close immediately.`)) return;
    setError(null);
    setMessage(null);

    try {
      const { error: updateError } = await supabase
        .from("feedback_cycles")
        .update({ closes_at: new Date().toISOString() })
        .eq("id", cycleId);

      if (updateError) throw updateError;

      setMessage(`Cycle "${cycleName}" ended.`);
      await loadSessionData(selectedSessionId);
      await refreshPerformanceCyclesDropdown();
    } catch (caughtError) {
      setError(caughtError.message || "Could not end feedback cycle.");
    }
  }

  async function handleCancelCycle(cycleId, cycleName) {
    if (!window.confirm(`Cancel the scheduled request for "${cycleName}"? This cannot be undone.`)) return;
    setError(null);
    setMessage(null);

    try {
      const { error: deleteError } = await supabase
        .from("feedback_cycles")
        .delete()
        .eq("id", cycleId);

      if (deleteError) throw deleteError;

      setMessage(`Cycle request "${cycleName}" cancelled.`);
      await loadSessionData(selectedSessionId);
      await refreshPerformanceCyclesDropdown();
    } catch (caughtError) {
      setError(caughtError.message || "Could not cancel feedback cycle.");
    }
  }

  async function handleCalculateLeaderboard() {
    if (!selectedCycleId) return;
    setError(null);
    setMessage(null);
    setIsCalculating(true);

    try {
      const { error: rpcError } = await supabase.rpc("generate_leaderboard_snapshot", {
        target_cycle_id: selectedCycleId,
      });

      if (rpcError) throw rpcError;

      await loadPerformanceData();
      setSnapshotExists(true);
      setMessage("Leaderboard snapshot generated and saved successfully.");
    } catch (caughtError) {
      setError(caughtError.message || "Could not generate leaderboard snapshot.");
    } finally {
      setIsCalculating(false);
    }
  }

  async function handleExportLeaderboard() {
    if (!isSupabaseConfigured) {
      setError("Supabase is not configured yet.");
      return;
    }
    if (!performanceSessionId) {
      setError("Please select an academic session first.");
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      // 1. Fetch all mentors from the db (mentors table)
      const allDbMentors = await fetchAllRows((from, to) =>
        supabase
          .from("mentors")
          .select("id, name, roll_number, email, phone_number, branch")
          .order("name", { ascending: true })
          .range(from, to),
      );

      // 2. Obtain leaderboard performance scores for the selected session & cycle
      let activeScores = leaderboardRows;

      // If leaderboardRows is empty, attempt to fetch snapshot or compute
      if (!activeScores || !activeScores.length) {
        if (selectedCycleId) {
          const snapshots = await fetchAllRows((from, to) =>
            supabase
              .from("leaderboard_snapshots")
              .select(
                "id, mentor_id, session_id, cycle_id, review, engagement_score, final_score, mentors(name, roll_number)",
              )
              .eq("cycle_id", selectedCycleId)
              .range(from, to),
          );
          if (snapshots && snapshots.length) {
            activeScores = snapshots;
          }
        }
      }

      // Build lookup by mentor ID and roll number
      const scoreMap = new Map();
      (activeScores || []).forEach((row) => {
        const id = row.mentor_id || row.id;
        if (id) {
          scoreMap.set(String(id), row);
        }
        const roll = row.mentors?.roll_number;
        if (roll) {
          scoreMap.set(String(roll).trim().toUpperCase(), row);
        }
      });

      // Map to ensure all mentors from db are present
      const mentorsMap = new Map();
      (allDbMentors || []).forEach((m) => {
        mentorsMap.set(String(m.id), m);
      });

      // Also include any mentor from activeScores that might not be in allDbMentors
      (activeScores || []).forEach((r) => {
        const id = r.mentor_id || r.id;
        if (id && !mentorsMap.has(String(id))) {
          mentorsMap.set(String(id), {
            id,
            name: r.mentors?.name || "Unknown mentor",
            roll_number: r.mentors?.roll_number || "-",
            email: "-",
            phone_number: "-",
            branch: "-",
          });
        }
      });

      // Combine mentors with their performance scores
      const combined = Array.from(mentorsMap.values()).map((mentor) => {
        const scoreData =
          scoreMap.get(String(mentor.id)) ||
          (mentor.roll_number
            ? scoreMap.get(String(mentor.roll_number).trim().toUpperCase())
            : null);

        const review =
          scoreData && scoreData.review !== null && scoreData.review !== undefined
            ? Number(scoreData.review)
            : 0;
        const engagement =
          scoreData && scoreData.engagement_score !== null && scoreData.engagement_score !== undefined
            ? Number(scoreData.engagement_score)
            : 0;
        const finalScore =
          scoreData && scoreData.final_score !== null && scoreData.final_score !== undefined
            ? Number(scoreData.final_score)
            : 0;

        return {
          mentor,
          review,
          engagement_score: engagement,
          final_score: finalScore,
        };
      });

      // Sort by final_score descending (top performers first)
      combined.sort((a, b) => {
        if (b.final_score !== a.final_score) {
          return b.final_score - a.final_score;
        }
        if (b.review !== a.review) {
          return b.review - a.review;
        }
        if (b.engagement_score !== a.engagement_score) {
          return b.engagement_score - a.engagement_score;
        }
        return (a.mentor.name || "").localeCompare(b.mentor.name || "");
      });

      // Compute competition rank
      let currentRank = 1;
      for (let i = 0; i < combined.length; i += 1) {
        if (i > 0 && combined[i].final_score < combined[i - 1].final_score) {
          currentRank = i + 1;
        }
        combined[i].rank = currentRank;
      }

      // Format strictly mentor leaderboard rows (no individual mentee feedbacks)
      const exportRows = combined.map((item) => ({
        Rank: item.rank,
        "Mentor Name": item.mentor.name || "-",
        "Roll Number": item.mentor.roll_number || "-",
        Email: item.mentor.email || "-",
        "Phone Number": item.mentor.phone_number || "-",
        Department: item.mentor.branch || "-",
        "Average Rating (out of 5)": Number(item.review.toFixed(2)),
        "Engagement Score (%)": Number(item.engagement_score.toFixed(1)),
        "Final Score": Number(item.final_score.toFixed(1)),
      }));

      // Create Excel workbook
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      worksheet["!cols"] = [
        { wch: 8 },  // Rank
        { wch: 25 }, // Mentor Name
        { wch: 15 }, // Roll Number
        { wch: 30 }, // Email
        { wch: 16 }, // Phone Number
        { wch: 24 }, // Department
        { wch: 25 }, // Average Rating (out of 5)
        { wch: 22 }, // Engagement Score (%)
        { wch: 14 }, // Final Score
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leaderboard");

      const sessionObj = sessions.find((s) => s.id === performanceSessionId);
      const sessionName = sessionObj
        ? sessionObj.session_name.replace(/[^a-zA-Z0-9_-]/g, "_")
        : "session";
      const cycleObj = performanceCycles.find((c) => c.id === selectedCycleId);
      const cycleName = cycleObj
        ? cycleObj.cycle_name.replace(/[^a-zA-Z0-9_-]/g, "_")
        : "Overall";
      const dateStr = new Date().toISOString().slice(0, 10);
      const fileName = `Mentor_Leaderboard_${sessionName}_${cycleName}_${dateStr}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      setMessage(`Successfully exported complete leaderboard (${exportRows.length} mentors) to ${fileName}`);
    } catch (caughtError) {
      setError(caughtError.message || "Could not export leaderboard to Excel.");
    } finally {
      setIsExporting(false);
    }
  }

  async function handleViewDetails(mentorRow) {
    setSelectedMentor(mentorRow);
    setModalFeedbacks([]);
    setIsLoadingModal(true);

    try {
      let query = supabase
        .from("feedbacks")
        .select("id, mentee_id, q1_rating, q2_rating, q3_rating, q4_rating, q5_rating, created_at, mentees(name, roll_number)")
        .eq("mentor_id", mentorRow.mentor_id)
        .order("created_at", { ascending: false });

      if (selectedCycleId) {
        query = query.eq("cycle_id", selectedCycleId);
      }

      const { data, error: fbError } = await query;
      if (fbError) throw fbError;
      setModalFeedbacks(data || []);
    } catch (caughtError) {
      setError(caughtError.message || "Could not load feedback details.");
    } finally {
      setIsLoadingModal(false);
    }
  }

  async function handleRemindMentees() {
    if (!selectedCycleId) {
      alert("Please select a Target Cycle.");
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert("Please provide both a subject and a body for the email.");
      return;
    }
    if (!window.confirm("Are you sure you want to queue email reminders to all unresponsive mentees?")) {
      return;
    }

    setIsQueueing(true);
    try {
      const { data: campaign, error: campaignError } = await supabase
        .from('email_campaigns')
        .insert([{ subject: emailSubject, body: emailBody }])
        .select('id')
        .single();
        
      if (campaignError) throw campaignError;
      
      const { error: rpcError } = await supabase.rpc('queue_mentee_reminders', {
        p_cycle_id: selectedCycleId,
        p_campaign_id: campaign.id
      });
      
      if (rpcError) throw rpcError;
      
      alert(`Successfully queued mentee reminders in the background!`);
      setEmailSubject("");
      setEmailBody("");
    } catch (err) {
      console.error("Failed to queue mentees:", err);
      alert(`An error occurred: ${err.message}`);
    } finally {
      setIsQueueing(false);
    }
  }

  async function handleRemindMentors() {
    if (!selectedCycleId) {
      alert("Please select a Target Cycle.");
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert("Please provide both a subject and a body for the email.");
      return;
    }
    if (!window.confirm("Are you sure you want to queue email reminders to the mentors of all unresponsive mentees?")) {
      return;
    }

    setIsQueueing(true);
    try {
      const { data: campaign, error: campaignError } = await supabase
        .from('email_campaigns')
        .insert([{ subject: emailSubject, body: emailBody }])
        .select('id')
        .single();
        
      if (campaignError) throw campaignError;
      
      const { error: rpcError } = await supabase.rpc('queue_mentor_reminders', {
        p_cycle_id: selectedCycleId,
        p_campaign_id: campaign.id
      });
      
      if (rpcError) throw rpcError;
      
      alert(`Successfully queued mentor reminders in the background!`);
      setEmailSubject("");
      setEmailBody("");
    } catch (err) {
      console.error("Failed to queue mentors:", err);
      alert(`An error occurred: ${err.message}`);
    } finally {
      setIsQueueing(false);
    }
  }

  async function handleImportFile(event) {
    const file = event.target.files?.[0];
    setError(null);
    setMessage(null);
    setImportRows([]);
    setImportFile(null);
    setImportProgress(null);

    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    let rawRows = [];

    if (extension === "xlsx") {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      for (const sheetName of workbook.SheetNames) {
        const worksheet = workbook.Sheets[sheetName];
        const sheetRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        rawRows.push(...sheetRows);
      }
    } else if (extension === "csv") {
      const text = await file.text();
      rawRows = parseCsv(text);
    } else {
      setError("Upload a .csv or .xlsx roster file.");
      return;
    }

    if (!rawRows.length) {
      setError("The file appears to be empty.");
      return;
    }

    // Normalize all column headers using alias mapping and filter out empty rows
    const formattedRows = rawRows
      .map((row) => {
        const normalized = {};
        Object.keys(row).forEach((key) => {
          normalized[normalizeHeader(key)] = row[key];
        });
        return normalized;
      })
      .filter((row) =>
        Boolean(
          String(row.mentee_roll_no || "").trim() ||
            String(row.mentee_name || "").trim() ||
            String(row.mentor_roll || "").trim()
        )
      );

    const missingColumns = importRequiredColumns.filter(
      (column) => !(column in (formattedRows[0] || {})),
    );

    if (missingColumns.length) {
      setError(`Missing required columns: ${missingColumns.join(", ")}.`);
      return;
    }

    setImportRows(formattedRows);
    setMessage(`${formattedRows.length} roster rows loaded for review.`);
  }

  async function handleImportRoster() {
    if (
      !importSessionName.trim() ||
      !importRows.length ||
      !isValidSessionName(importSessionName)
    ) {
      setError("Provide a valid session name (YYYY-YY format) and select a file.");
      return;
    }

    setError(null);
    setMessage(null);
    setIsImporting(true);

    const totalBatches = Math.ceil(importRows.length / BATCH_SIZE);
    setImportProgress({ current: 0, total: totalBatches, percent: 0 });

    try {
      let sessionId = null;
      let isUpdate = false;

      for (let i = 0; i < importRows.length; i += BATCH_SIZE) {
        const batch = importRows.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;

        const { data, error: fnError } = await supabase.functions.invoke(
          "import-roster",
          { body: { session_name: importSessionName.trim(), rows: batch } },
        );

        if (fnError) throw fnError;
        if (data?.error) throw new Error(data.error);

        sessionId = data.session_id;
        isUpdate = data.is_update;

        setImportProgress({
          current: batchNumber,
          total: totalBatches,
          percent: Math.round((batchNumber / totalBatches) * 100),
        });
      }

      // Compute summary from client-side data (more accurate than per-batch counts)
      const uniqueMentorRolls = new Set(importRows.map((r) => r.mentor_roll));

      setImportRows([]);
      setImportSessionName("");
      setImportProgress(null);
      setMessage(
        isUpdate
          ? `Updated allocations: ${uniqueMentorRolls.size} mentors and ${importRows.length} mentees processed.`
          : `Created session and imported ${uniqueMentorRolls.size} mentors and ${importRows.length} mentees.`,
      );
      await loadAdminData();
      if (sessionId) setSelectedSessionId(sessionId);
    } catch (caughtError) {
      setError(caughtError.message || "Could not import roster.");
      setImportProgress(null);
    } finally {
      setIsImporting(false);
    }
  }

  const card = {
    borderRadius: "8px",
    border: "1px solid #dfe8ee",
    background: "#fff",
    padding: "18px",
    boxShadow: "0 10px 26px rgba(36,55,70,0.06)",
  };

  const field = {
    minHeight: "42px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    padding: "8px 12px",
    font: "inherit",
    color: "#00293A",
    background: "#fff",
  };

  const label = {
    display: "grid",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 700,
    color: "#334155",
  };

  return (
    <PageShell
      actions={
        <Button icon={RefreshCw} onClick={loadAdminData} variant="secondary">
          Refresh
        </Button>
      }
      eyebrow="Mentorship Admin"
      title="Admin Panel"
    >
      {isLoading ? (
        <div style={card}>Loading admin panel...</div>
      ) : null}

      {message ? (
        <div style={{ marginBottom: "16px" }}>
          <StatusBanner icon={CheckCircle2} tone="green">{message}</StatusBanner>
        </div>
      ) : null}

      {error ? (
        <div style={{ marginBottom: "16px" }}>
          <StatusBanner tone="amber">{error}</StatusBanner>
        </div>
      ) : null}

      <div className="mentorship-admin-grid">
        <section style={card}>
          <div className="mentorship-admin-section-title">
            <Calendar />
            <div>
              <h2>Sessions and Feedback Cycles</h2>
              <p>Create sessions, set the active session, and schedule request windows.</p>
            </div>
          </div>

          <div className="mentorship-admin-form">
            <label style={label}>
              Academic session
              <select
                onChange={(event) => setSelectedSessionId(event.target.value)}
                style={field}
                value={selectedSessionId}
              >
                <option value="">Select session</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.session_name}{session.is_current ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <Button disabled={!selectedSessionId || selectedSession?.is_current} onClick={handleSetCurrentSession} variant="secondary">
              Set current
            </Button>
          </div>



          <form className="mentorship-admin-cycle-form" onSubmit={handleCreateCycle}>
            {/* Row 1: Cycle name */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={label}>
                Cycle name
                <input
                  onChange={(event) => setCycleForm((current) => ({ ...current, cycle_name: event.target.value }))}
                  placeholder="Cycle 1"
                  style={field}
                  value={cycleForm.cycle_name}
                />
              </label>
            </div>

            {/* Row 2: Opens at + Closes at side-by-side */}
            <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <label style={label}>
                Schedule Request Mails (Opens at)
                <input
                  onChange={(event) => setCycleForm((current) => ({ ...current, opens_at: event.target.value }))}
                  style={field}
                  type="datetime-local"
                  value={cycleForm.opens_at}
                />
              </label>
              <label style={label}>
                Mails/Request Closes at
                <input
                  onChange={(event) => setCycleForm((current) => ({ ...current, closes_at: event.target.value }))}
                  style={field}
                  type="datetime-local"
                  value={cycleForm.closes_at}
                />
              </label>
            </div>

            {/* Row 3: Submit button */}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
              <Button disabled={!selectedSessionId} icon={Calendar} isLoading={isSavingCycle} type="submit">
                Schedule Requests
              </Button>
            </div>
          </form>

          {(() => {
            const now = new Date();
            const pendingCycles = cycles.filter((cycle) => new Date(cycle.closes_at) > now);
            if (!pendingCycles.length) {
              return (
                <div className="mentorship-admin-note" style={{ marginTop: "12px" }}>
                  No pending feedback cycles. Schedule a new one above.
                </div>
              );
            }
            return (
              <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {pendingCycles.map((cycle) => {
                  const isActive = new Date(cycle.opens_at) <= now && new Date(cycle.closes_at) > now;
                  return (
                    <div
                      key={cycle.id}
                      style={{
                        padding: "8px 14px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        background: isActive ? "#e0f7f1" : "#f8fafc",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "0.85rem",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: isActive ? "#0d9488" : "#94a3b8",
                          flexShrink: 0,
                        }}
                      />
                      <strong>{cycle.cycle_name}</strong>
                      <span style={{ color: "#64748b", fontSize: "0.8rem" }}>
                        {isActive ? "Active" : "Upcoming"} · closes {new Date(cycle.closes_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      {isActive ? (
                        <button
                          onClick={() => handleEndCycle(cycle.id, cycle.cycle_name)}
                          title="End this cycle now"
                          type="button"
                          style={{
                            padding: "2px 8px",
                            fontSize: "11px",
                            border: "1px solid #f59e0b",
                            borderRadius: "4px",
                            background: "#fffbeb",
                            cursor: "pointer",
                            color: "#b45309",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          End cycle
                        </button>
                      ) : (
                        <button
                          onClick={() => handleCancelCycle(cycle.id, cycle.cycle_name)}
                          title="Cancel this scheduled cycle request"
                          type="button"
                          style={{
                            padding: "2px 8px",
                            fontSize: "11px",
                            border: "1px solid #fca5a5",
                            borderRadius: "4px",
                            background: "#fef2f2",
                            cursor: "pointer",
                            color: "#b91c1c",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Cancel request
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </section>

        <section style={card}>
          <div className="mentorship-admin-section-title">
            <FileText />
            <div>
              <h2>Roster Import & Session Management</h2>
              <p>Upload an Excel roster to create or update mentor-mentee allocations.</p>
            </div>
          </div>

          {/* ── Academic Session Input with YYYY-YY validation ── */}
          <div style={{ marginBottom: "16px" }}>
            <label style={label}>
              Academic Session (YYYY-YY)
              <input
                onChange={(event) => setImportSessionName(event.target.value)}
                placeholder="e.g. 2025-26"
                style={{
                  ...field,
                  borderColor:
                    importSessionName.trim() && !isValidSessionName(importSessionName)
                      ? "#dc2626"
                      : field.borderColor,
                }}
                value={importSessionName}
              />
            </label>
            {importSessionName.trim() && !isValidSessionName(importSessionName) ? (
              <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>
                Use YYYY-YY format where YY = last two digits of YYYY + 1 (e.g. 2025-26)
              </div>
            ) : null}
          </div>

          {/* ── Existing session quick-select chips ── */}
          {sessions.length > 0 ? (
            <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>Quick select:</span>
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setImportSessionName(s.session_name)}
                  type="button"
                  style={{
                    padding: "4px 12px",
                    borderRadius: "999px",
                    border: importSessionName.trim() === s.session_name ? "1.5px solid #028D93" : "1px solid #cbd5e1",
                    background: importSessionName.trim() === s.session_name ? "#e0f7f1" : "#f8fafc",
                    color: importSessionName.trim() === s.session_name ? "#028D93" : "#334155",
                    fontSize: "13px",
                    cursor: "pointer",
                    fontWeight: importSessionName.trim() === s.session_name ? 600 : 400,
                    transition: "all 0.15s ease",
                  }}
                >
                  {s.session_name}
                </button>
              ))}
            </div>
          ) : null}

          {/* ── Session-exists info banner ── */}
          {importSessionName.trim() && isValidSessionName(importSessionName) && sessionExists ? (
            <div style={{
              padding: "10px 14px",
              borderRadius: "8px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              fontSize: "13px",
              color: "#1e40af",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}>
              <span>ℹ️</span>
              <span>Session <strong>"{importSessionName.trim()}"</strong> already exists — uploading will update existing mentor-mentee allocations.</span>
            </div>
          ) : null}

          {/* ── Upload zone ── */}
          <label className="mentorship-upload-zone">
            <Upload />
            <span>Upload Excel/CSV roster</span>
            <input accept=".csv,.xlsx" onChange={handleImportFile} type="file" />
          </label>

          <div className="mentorship-admin-note">
            Required columns: <strong>{importRequiredColumns.join(", ")}</strong>.
            Column headers are flexible — e.g. "Alloted Mentor Name", "Mentor Name", or "mentor_name" all work.
          </div>

          {/* ── Preview + Progress + Import button ── */}
          {importRows.length ? (
            <>
              <div className="mentorship-import-preview">
                {importRows.slice(0, 5).map((row, index) => (
                  <div key={`${row.mentor_roll}-${row.mentee_roll_no}-${index}`}>
                    <strong>{row.mentor_name}</strong>
                    <span>{row.mentor_roll} → {row.mentee_name} ({row.mentee_roll_no})</span>
                  </div>
                ))}
                {importRows.length > 5 ? (
                  <div style={{ color: "#64748b", fontSize: "13px", padding: "4px 0" }}>
                    …and {importRows.length - 5} more rows
                  </div>
                ) : null}
              </div>

              {importProgress ? (
                <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px", color: "#334155" }}>
                    <span>Importing batch {importProgress.current} of {importProgress.total}…</span>
                    <span style={{ fontWeight: 600 }}>{importProgress.percent}%</span>
                  </div>
                  <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: `${importProgress.percent}%`,
                      background: "linear-gradient(90deg, #028D93, #0ea5e9)",
                      borderRadius: "999px",
                      transition: "width 0.3s ease",
                    }} />
                  </div>
                </div>
              ) : null}

              <Button
                disabled={!importSessionName.trim() || !isValidSessionName(importSessionName)}
                icon={Upload}
                isLoading={isImporting}
                onClick={handleImportRoster}
              >
                {sessionExists ? "Update Allocations" : "Generate Session"} · {importRows.length} rows
              </Button>
            </>
          ) : null}
        </section>
      </div>

      <section style={{ ...card, marginTop: "20px" }}>
        <div className="mentorship-admin-section-title">
          <BarChart />
          <div>
            <h2>Mentor Performance</h2>
            <p>Worst performers are shown first for admin review.</p>
          </div>
        </div>

        <div className="mentorship-admin-form">
          <label style={label}>
            Academic Session
            <select
              onChange={(event) => {
                 setPerformanceSessionId(event.target.value);
                 setSelectedCycleId("");
              }}
              style={field}
              value={performanceSessionId}
            >
              <option value="">Select session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.session_name}{session.is_current ? " (current)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label style={label}>
            Snapshot Cycle
            <select
              onChange={(event) => setSelectedCycleId(event.target.value)}
              style={field}
              value={selectedCycleId}
            >
              <option value="">Overall session</option>
              {performanceCycles.map((cycle) => (
                <option key={cycle.id} value={cycle.id}>{cycle.cycle_name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* ---- Cycle Management: Calculate Leaderboard ---- */}
        {(() => {
          const perfCycle = performanceCycles.find((c) => c.id === selectedCycleId);
          const isCycleActive = perfCycle && new Date(perfCycle.closes_at) > new Date();
          const isCycleClosed = perfCycle && new Date(perfCycle.closes_at) <= new Date();
          const showCalculateButton = selectedCycleId && isCycleClosed && !snapshotExists;

          return (
            <div
              style={{
                padding: "14px 18px",
                borderRadius: "8px",
                border: "1px solid #dfe8ee",
                background: isCycleActive ? "#fffbeb" : snapshotExists ? "#f0fdf4" : "#f0fdfa",
                marginBottom: "18px",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div style={{ flex: "1 1 auto", minWidth: "200px" }}>
                <strong style={{ color: "#00293A", fontSize: "14px" }}>Cycle Management</strong>
                {isCycleActive ? (
                  <p style={{ margin: "6px 0 0", color: "#92400e", fontSize: "13px", lineHeight: 1.5 }}>
                    ⚠ This cycle is currently active. Snapshots cannot be generated until it closes.
                  </p>
                ) : snapshotExists ? (
                  <p style={{ margin: "6px 0 0", color: "#166534", fontSize: "13px", lineHeight: 1.5 }}>
                    ✓ Leaderboard snapshot has been generated for this cycle.
                  </p>
                ) : (
                  <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "13px", lineHeight: 1.5 }}>
                    {selectedCycleId
                      ? "This cycle has closed. Generate the leaderboard snapshot now."
                      : "Select a specific cycle above to generate a snapshot."}
                  </p>
                )}
              </div>
              {showCalculateButton ? (
                <Button
                  icon={BarChart}
                  isLoading={isCalculating}
                  onClick={handleCalculateLeaderboard}
                >
                  Calculate Leaderboard
                </Button>
              ) : null}
            </div>
          );
        })()}

        {/* Controls: Search, Sort, Items per page */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 240px", maxWidth: "340px" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                pointerEvents: "none",
              }}
            />
            <input
              placeholder="Search mentor by name or roll..."
              type="text"
              value={leaderboardSearch}
              onChange={(e) => {
                setLeaderboardSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                ...field,
                width: "100%",
                paddingLeft: "36px",
                height: "38px",
                minHeight: "38px",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ ...label, display: "flex", flexDirection: "row", alignItems: "center", gap: "6px", margin: 0 }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>Sort:</span>
              <select
                value={leaderboardSort}
                onChange={(e) => {
                  setLeaderboardSort(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ ...field, height: "38px", minHeight: "38px", padding: "6px 10px", fontSize: "13px" }}
              >
                <option value="worst_first">Lowest Score First (Default)</option>
                <option value="best_first">Highest Score First</option>
                <option value="name_asc">Mentor Name (A–Z)</option>
                <option value="name_desc">Mentor Name (Z–A)</option>
                <option value="rating_desc">Highest Rating</option>
                <option value="engagement_desc">Highest Engagement</option>
              </select>
            </label>

            <label style={{ ...label, display: "flex", flexDirection: "row", alignItems: "center", gap: "6px", margin: 0 }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>Per Page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ ...field, height: "38px", minHeight: "38px", padding: "6px 10px", fontSize: "13px" }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500 (All)</option>
              </select>
            </label>
          </div>
        </div>

        <div className="mentorship-admin-table-wrap" style={{ maxHeight: "500px", overflowY: "auto" }}>
          <table className="mentorship-admin-table" style={{ position: "relative" }}>
            <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <tr>
                <th>Mentor Name</th>
                <th>Average Rating</th>
                <th>Engagement Score</th>
                <th>Final Score</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeaderboardRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.mentors?.name || "Unknown mentor"}</strong>
                    <span>{row.mentors?.roll_number || "-"}</span>
                  </td>
                  <td>{formatRating(row.review)}</td>
                  <td>{formatPercent(row.engagement_score)}</td>
                  <td>{formatScore(row.final_score)}</td>
                  <td>
                    <Button icon={Eye} onClick={() => handleViewDetails(row)} variant="secondary">
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
              {!filteredAndSortedLeaderboard.length ? (
                <tr>
                  <td colSpan="5">
                    {leaderboardRows.length
                      ? "No mentors match your search query."
                      : "No snapshot found. Select a cycle and generate the leaderboard after it closes."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {filteredAndSortedLeaderboard.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              marginTop: "14px",
              paddingTop: "12px",
              borderTop: "1px solid #e2e8f0",
              fontSize: "13px",
              color: "#475569",
            }}
          >
            <div>
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> –{" "}
              <strong>
                {Math.min(currentPage * pageSize, filteredAndSortedLeaderboard.length)}
              </strong>{" "}
              of <strong>{filteredAndSortedLeaderboard.length}</strong> mentors
              {leaderboardRows.length !== filteredAndSortedLeaderboard.length ? (
                <span> (filtered from {leaderboardRows.length})</span>
              ) : null}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Button
                disabled={currentPage <= 1}
                icon={ChevronLeft}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                style={{ padding: "6px 12px", minHeight: "34px" }}
                variant="secondary"
              >
                Prev
              </Button>
              <span style={{ fontWeight: 600, padding: "0 6px" }}>
                Page {currentPage} of {totalLeaderboardPages}
              </span>
              <Button
                disabled={currentPage >= totalLeaderboardPages}
                icon={ChevronRight}
                onClick={() => setCurrentPage((p) => Math.min(totalLeaderboardPages, p + 1))}
                style={{ padding: "6px 12px", minHeight: "34px" }}
                variant="secondary"
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}

        {/* ---- Export Leaderboard to Excel ---- */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginTop: "16px",
            paddingTop: "14px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <div>
            <strong style={{ color: "#00293A", fontSize: "14px" }}>Export Complete Leaderboard</strong>
            <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: "13px" }}>
              Download all mentors from the database with their rankings and performance scores as an Excel (.xlsx) file.
            </p>
          </div>
          <Button
            disabled={!performanceSessionId || isExporting}
            icon={Download}
            isLoading={isExporting}
            onClick={handleExportLeaderboard}
            variant="secondary"
          >
            Export to Excel
          </Button>
        </div>
      </section>

      {selectedMentor ? (
        <div className="mentorship-admin-modal" role="dialog" aria-modal="true">
          <div className="mentorship-admin-modal-card">
            <div className="mentorship-admin-modal-header">
              <div>
                <p className="mentorship-eyebrow">Raw Feedback</p>
                <h2>{selectedMentor.mentors?.name || "Mentor"}</h2>
                <span>
                  {performanceCycles.find((c) => c.id === selectedCycleId)?.cycle_name || "Overall session"}
                  {" · "}
                  Avg Rating: {formatRating(selectedMentor.review)}
                  {" · "}
                  Final Score: {formatScore(selectedMentor.final_score)}
                </span>
              </div>
              <button onClick={() => setSelectedMentor(null)} type="button">
                <X />
              </button>
            </div>

            {isLoadingModal ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
                Loading feedback data...
              </div>
            ) : (
              <div className="mentorship-admin-table-wrap">
                <table className="mentorship-admin-table">
                  <thead>
                    <tr>
                      <th>Mentee</th>
                      <th>Q1</th>
                      <th>Q2</th>
                      <th>Q3</th>
                      <th>Q4</th>
                      <th>Q5</th>
                      <th>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalFeedbacks.map((feedback) => (
                      <tr key={feedback.id}>
                        <td>
                          <strong>{feedback.mentees?.name || "Mentee"}</strong>
                          <span>{feedback.mentees?.roll_number || "-"}</span>
                        </td>
                        <td>{feedback.q1_rating}</td>
                        <td>{feedback.q2_rating}</td>
                        <td>{feedback.q3_rating}</td>
                        <td>{feedback.q4_rating}</td>
                        <td>{feedback.q5_rating}</td>
                        <td>{getAverage(feedback)?.toFixed(2) || "-"}</td>
                      </tr>
                    ))}
                    {!modalFeedbacks.length ? (
                      <tr>
                        <td colSpan="7">No raw feedback rows found for this mentor.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <section style={{ ...card, marginTop: "20px" }}>
        <div className="mentorship-admin-section-title">
          <Mail />
          <div>
            <h2>Email Communications</h2>
            <p style={{ margin: 0, color: "#64748b" }}>
              Send reminders and updates directly to mentees via your default email client (e.g. Gmail).
            </p>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", paddingBottom: "16px", borderBottom: "1px solid #e2e8f0" }}>
            <label style={label}>
              Target Session
              <select
                style={field}
                value={performanceSessionId}
                onChange={(e) => setPerformanceSessionId(e.target.value)}
              >
                <option value="">Select Session...</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.session_name}
                  </option>
                ))}
              </select>
            </label>

            <label style={label}>
              Target Cycle
              <select
                style={field}
                value={selectedCycleId}
                onChange={(e) => setSelectedCycleId(e.target.value)}
                disabled={!performanceSessionId}
              >
                <option value="">Overall Session</option>
                {performanceCycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.cycle_name}
                  </option>
                ))}
              </select>
            </label>
          </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <label style={label}>
            Email Subject
            <input 
              required
              placeholder="e.g. Action Required: Mentorship Feedback"
              style={field}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </label>

          <label style={label}>
            Email Body
            <textarea 
              required
              placeholder="Write your email here..."
              style={{ ...field, minHeight: "120px", resize: "vertical" }}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
          </label>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
            <Button onClick={handleRemindMentees} disabled={isQueueing || !selectedCycleId}>
              <Mail size={18} />
              {isQueueing ? "Queueing..." : "Remind Mentees"}
            </Button>
            <Button onClick={handleRemindMentors} disabled={isQueueing || !selectedCycleId}>
              <Send size={18} />
              {isQueueing ? "Queueing..." : "Remind Mentors"}
            </Button>
          </div>
        </div>
        </div>
      </section>
    </PageShell>
  );
}
